import {
    IpxCredentialFieldClassification,
    IpxCredentialFieldNamePattern,
    IpxCredentialFieldKind,
    type IpxCredentialFieldConfig,
    type IpxCredentialFieldConfigMap,
} from './secret.types';

export type RpaDomElementAction =
    | 'wait'
    | 'read'
    | 'click'
    | 'right_click'
    | 'double_click'
    | 'scroll_to_bottom'
    | 'set_text'
    | 'paste_text'
    | 'type_text'
    | 'keypress';

export type GenericUxKeyModifier = 'Alt' | 'Control' | 'Meta' | 'Shift';

export interface GenericUxSequenceStep {
    action: RpaDomElementAction;
    selector?: string;
    framePath?: number[];
    timeoutMs?: number;
    text?: string;
    delayMs?: number;
    key?: string;
    modifiers?: GenericUxKeyModifier[];
    milliseconds?: number;
}

export interface RpaDomSelector {
    readonly selector: string;
    readonly innerText?: string;
    readonly textContent?: string;
    readonly framePath?: number[];
}

export type RpaDomSelectorFieldMap = Record<string, RpaDomSelector>;

export type RpaScreenKey = string;

export interface RpaDomTextScreenDiscriminantExpression {
    readonly kind: 'dom_text';
    readonly selector: RpaDomSelector;
    readonly normalizer: 'visible_text_v1';
}

export interface RpaDomAttributeScreenDiscriminantExpression {
    readonly kind: 'dom_attribute';
    readonly selector: RpaDomSelector;
    readonly attribute: string;
    readonly normalizer: 'trim_v1';
}

export type RpaScreenDiscriminantExpression =
    | RpaDomTextScreenDiscriminantExpression
    | RpaDomAttributeScreenDiscriminantExpression;

export interface RpaScreenDetectionPredicate {
    readonly expression: RpaScreenDiscriminantExpression;
    readonly expected_value: string;
}

export interface RpaScreenDefinition {
    readonly screen_key: RpaScreenKey;
    readonly display_name: string;
    readonly detection_predicate?: RpaScreenDetectionPredicate;
}

export interface RpaScreenDetectionProfile {
    readonly screens: Record<RpaScreenKey, RpaScreenDefinition>;
}

export type RpaScreenDiscriminantProfile = RpaScreenDetectionProfile;

export type RpaSanitizedDomStableAttributeMap = Record<string, string>;

export interface RpaSanitizedDomSnapshot {
    readonly tag_name: string;
    readonly stable_attributes: RpaSanitizedDomStableAttributeMap;
    readonly visible_text?: string;
    readonly children: readonly RpaSanitizedDomSnapshot[];
}

export interface RpaCredentialFieldSelector extends RpaDomSelector {
    readonly required_screen_key?: RpaScreenKey;
}

export type RpaCredentialFieldSelectorMap = Record<string, RpaCredentialFieldSelector>;

export const IpxWebResourceElementKind = {
    SCREEN_MARKER: 'screen_marker',
    TEXT: 'text',
    FIELD: 'field',
    SECRET: 'secret',
    BUTTON: 'button',
} as const;
export type IpxWebResourceElementKindKey = keyof typeof IpxWebResourceElementKind;
export type IpxWebResourceElementKind =
    (typeof IpxWebResourceElementKind)[keyof typeof IpxWebResourceElementKind];

export interface IpxWebResourceElementEvidence {
    readonly stable_text?: readonly string[];
    readonly stable_attributes?: RpaSanitizedDomStableAttributeMap;
}

export const IpxWebResourceElementKindSupportedActions = {
    SCREEN_MARKER: ['wait', 'keypress'],
    TEXT: ['wait', 'read', 'click', 'right_click', 'double_click'],
    FIELD: [
        'wait',
        'read',
        'click',
        'right_click',
        'double_click',
        'set_text',
        'paste_text',
        'type_text',
    ],
    SECRET: [
        'wait',
        'read',
        'click',
        'right_click',
        'double_click',
        'set_text',
        'paste_text',
        'type_text',
    ],
    BUTTON: ['wait', 'click', 'right_click', 'double_click'],
} as const satisfies Record<IpxWebResourceElementKindKey, readonly RpaDomElementAction[]>;
export type IpxWebResourceElementKindSupportedAction<KindKey extends IpxWebResourceElementKindKey> =
    (typeof IpxWebResourceElementKindSupportedActions)[KindKey][number];

export const IpxWebResourceElementKindDefaultAction = {
    SCREEN_MARKER: 'wait',
    TEXT: 'read',
    FIELD: 'set_text',
    SECRET: 'set_text',
    BUTTON: 'click',
} as const satisfies {
    readonly [KindKey in IpxWebResourceElementKindKey]: IpxWebResourceElementKindSupportedAction<KindKey>;
};

export const IpxWebResourceElementKindSupportedActionsByKind = {
    [IpxWebResourceElementKind.SCREEN_MARKER]:
        IpxWebResourceElementKindSupportedActions.SCREEN_MARKER,
    [IpxWebResourceElementKind.TEXT]: IpxWebResourceElementKindSupportedActions.TEXT,
    [IpxWebResourceElementKind.FIELD]: IpxWebResourceElementKindSupportedActions.FIELD,
    [IpxWebResourceElementKind.SECRET]: IpxWebResourceElementKindSupportedActions.SECRET,
    [IpxWebResourceElementKind.BUTTON]: IpxWebResourceElementKindSupportedActions.BUTTON,
} as const satisfies Record<IpxWebResourceElementKind, readonly RpaDomElementAction[]>;
export type IpxWebResourceElementKindSupportedActionByKind<Kind extends IpxWebResourceElementKind> =
    (typeof IpxWebResourceElementKindSupportedActionsByKind)[Kind][number];

export const IpxWebResourceElementKindDefaultActionByKind = {
    [IpxWebResourceElementKind.SCREEN_MARKER]: IpxWebResourceElementKindDefaultAction.SCREEN_MARKER,
    [IpxWebResourceElementKind.TEXT]: IpxWebResourceElementKindDefaultAction.TEXT,
    [IpxWebResourceElementKind.FIELD]: IpxWebResourceElementKindDefaultAction.FIELD,
    [IpxWebResourceElementKind.SECRET]: IpxWebResourceElementKindDefaultAction.SECRET,
    [IpxWebResourceElementKind.BUTTON]: IpxWebResourceElementKindDefaultAction.BUTTON,
} as const satisfies {
    readonly [Kind in IpxWebResourceElementKind]: IpxWebResourceElementKindSupportedActionByKind<Kind>;
};

export type IpxWebResourceScreenMarkerKey = string;

export type IpxWebResourceScreenMarkerMap = Record<IpxWebResourceScreenMarkerKey, RpaDomSelector>;

// Traceability: implements OBL-RBS-LOC-CONTRACT from implementation RBS-WEBSITE-MAP-LOCATOR
// Finds a mapped element by what the user sees, not by generated ids: its
// role (explicit or implicit) and its accessible label without its value.
export interface IpxWebResourceElementLocator {
    readonly role: string;
    readonly label: string;
}

export interface IpxWebResourceDomElementDescriptor {
    readonly element_key: string;
    readonly display_name: string;
    readonly selector: RpaDomSelector;
    readonly element_kind: IpxWebResourceElementKind;
    readonly required_screen_marker_keys: readonly IpxWebResourceScreenMarkerKey[];
    readonly evidence?: IpxWebResourceElementEvidence;
    readonly notes?: string;
    readonly locator?: IpxWebResourceElementLocator;
    // The start of the path of the frame holding the element, such as
    // "/apps/payment/"; frames are found by it instead of by position.
    readonly frame_url_pattern?: string;
}

// The element shape stored by Website Map version 2; frozen.
export interface IpxWebResourceDomElementDescriptorV2 {
    readonly element_key: string;
    readonly display_name: string;
    readonly selector: RpaDomSelector;
    readonly element_kind: IpxWebResourceElementKind;
    readonly required_screen_marker_keys: readonly IpxWebResourceScreenMarkerKey[];
    readonly evidence?: IpxWebResourceElementEvidence;
    readonly notes?: string;
}

export type IpxWebResourceDomElementMapV2 = Record<string, IpxWebResourceDomElementDescriptorV2>;

export type IpxWebResourceDomElementMap = Record<string, IpxWebResourceDomElementDescriptor>;

export function listIpxWebResourceDomElements(
    document: IpxWebResourceModelDocument
): IpxWebResourceDomElementDescriptor[] {
    return Object.keys(document.dom_elements)
        .sort()
        .map((elementKey) => {
            const element = document.dom_elements[elementKey];
            if (element === undefined) {
                throw new Error(`Website Map DOM element ${elementKey} is missing.`);
            }
            return element;
        });
}

export function getIpxWebResourceDomElement(
    document: IpxWebResourceModelDocument,
    elementKey: string
): IpxWebResourceDomElementDescriptor | undefined {
    return document.dom_elements[elementKey];
}

export type IpxDomRpaWorkflowStepForKind<KindKey extends IpxWebResourceElementKindKey> = {
    readonly kind: (typeof IpxWebResourceElementKind)[KindKey];
    readonly element_key: string;
    readonly action: IpxWebResourceElementKindSupportedAction<KindKey>;
};

export type IpxDomRpaWorkflowStep = {
    readonly [KindKey in IpxWebResourceElementKindKey]: IpxDomRpaWorkflowStepForKind<KindKey>;
}[IpxWebResourceElementKindKey];

export type IpxDomRpaWorkflow = readonly IpxDomRpaWorkflowStep[];

export const IpxDomRpaWorkflowStorageVersion = {
    V1: 1,
    V2: 2,
} as const;
export type IpxDomRpaWorkflowStorageVersion =
    (typeof IpxDomRpaWorkflowStorageVersion)[keyof typeof IpxDomRpaWorkflowStorageVersion];

export type IpxDomRpaWorkflowV1 = IpxDomRpaWorkflow;

export interface IpxDomRpaWorkflowV2 {
    readonly workflow_version: typeof IpxDomRpaWorkflowStorageVersion.V2;
    readonly steps: IpxDomRpaWorkflow;
}

export type StoredIpxDomRpaWorkflow = IpxDomRpaWorkflowV1 | IpxDomRpaWorkflowV2;

export const IpxCredentialFieldNameFallback = {
    NONE: 'none',
    ELEMENT_KEY: 'element_key',
} as const;
export type IpxCredentialFieldNameFallback =
    (typeof IpxCredentialFieldNameFallback)[keyof typeof IpxCredentialFieldNameFallback];

function isCredentialWorkflowStep(step: IpxDomRpaWorkflowStep): boolean {
    return (
        step.kind === IpxWebResourceElementKind.FIELD ||
        step.kind === IpxWebResourceElementKind.SECRET
    );
}

export function isCredentialMapElement(element: IpxWebResourceDomElementDescriptor): boolean {
    return (
        element.element_kind === IpxWebResourceElementKind.FIELD ||
        element.element_kind === IpxWebResourceElementKind.SECRET
    );
}

function isCredentialFieldName(value: string): boolean {
    return value.length >= 1 && value.length <= 64 && IpxCredentialFieldNamePattern.test(value);
}

function resolveCredentialWorkflowElement(input: {
    readonly webResourceModel: IpxWebResourceModelDocument;
    readonly step: IpxDomRpaWorkflowStep;
}): IpxWebResourceDomElementDescriptor {
    const element = getIpxWebResourceDomElement(input.webResourceModel, input.step.element_key);
    if (element === undefined) {
        throw new Error(
            `Login workflow step references missing Website Map element ${input.step.element_key}.`
        );
    }
    if (!isCredentialMapElement(element)) {
        throw new Error(
            `Login workflow step ${input.step.element_key} does not reference a credential field element.`
        );
    }
    return element;
}

function inferCredentialFieldNameForElement(input: {
    readonly element: IpxWebResourceDomElementDescriptor;
    readonly fallback: IpxCredentialFieldNameFallback;
}): string {
    const element = input.element;
    const fieldName = element.display_name;
    if (isCredentialFieldName(fieldName)) {
        return fieldName;
    }
    if (
        input.fallback === IpxCredentialFieldNameFallback.ELEMENT_KEY &&
        isCredentialFieldName(element.element_key)
    ) {
        return element.element_key;
    }
    if (input.fallback === IpxCredentialFieldNameFallback.ELEMENT_KEY) {
        throw new Error(
            `Display Name "${fieldName}" and Website Map element key "${element.element_key}" cannot be used as credential field names.`
        );
    }
    throw new Error(`Display Name "${fieldName}" cannot be used as a credential field name.`);
}

export function getCredentialFieldNameForElement(
    element: IpxWebResourceDomElementDescriptor
): string {
    return inferCredentialFieldNameForElement({
        element,
        fallback: IpxCredentialFieldNameFallback.NONE,
    });
}

function getCredentialFieldNameFallback(
    fallback: IpxCredentialFieldNameFallback | undefined
): IpxCredentialFieldNameFallback {
    return fallback ?? IpxCredentialFieldNameFallback.NONE;
}

function buildCredentialFieldConfigForElementWithFallback(input: {
    readonly element: IpxWebResourceDomElementDescriptor;
    readonly fieldConfigMap: IpxCredentialFieldConfigMap;
    readonly fieldNameFallback: IpxCredentialFieldNameFallback;
}): IpxCredentialFieldConfig {
    const fieldName = inferCredentialFieldNameForElement({
        element: input.element,
        fallback: input.fieldNameFallback,
    });
    const existingConfig =
        input.fieldConfigMap[fieldName] ?? input.fieldConfigMap[input.element.element_key];
    return {
        field_name: fieldName,
        field_kind: existingConfig?.field_kind ?? IpxCredentialFieldKind.TEXT,
        field_classification:
            input.element.element_kind === IpxWebResourceElementKind.SECRET
                ? IpxCredentialFieldClassification.SECRET
                : IpxCredentialFieldClassification.PUBLIC,
        required: existingConfig?.required ?? true,
        display: {
            label: input.element.display_name,
        },
        validation: existingConfig?.validation ?? {},
        generation: existingConfig?.generation ?? {},
        rotatable: existingConfig?.rotatable ?? false,
        ...(existingConfig?.confirmation_field_name === undefined
            ? {}
            : {
                  confirmation_field_name: existingConfig.confirmation_field_name,
              }),
    };
}

export function buildCredentialFieldConfigForElement(input: {
    readonly element: IpxWebResourceDomElementDescriptor;
    readonly fieldConfigMap: IpxCredentialFieldConfigMap;
    readonly fieldNameFallback?: IpxCredentialFieldNameFallback;
}): IpxCredentialFieldConfig {
    return buildCredentialFieldConfigForElementWithFallback({
        element: input.element,
        fieldConfigMap: input.fieldConfigMap,
        fieldNameFallback: getCredentialFieldNameFallback(input.fieldNameFallback),
    });
}

function getWorkflowCredentialFieldNameForElement(input: {
    readonly element: IpxWebResourceDomElementDescriptor;
    readonly fieldNameFallback: IpxCredentialFieldNameFallback;
}): string {
    try {
        return inferCredentialFieldNameForElement({
            element: input.element,
            fallback: input.fieldNameFallback,
        });
    } catch (error) {
        throw new Error(
            error instanceof Error ? error.message : 'Unable to infer credential field name.'
        );
    }
}

export function updateCredentialFieldConfigMapForElement(input: {
    readonly element: IpxWebResourceDomElementDescriptor;
    readonly fieldConfigMap: IpxCredentialFieldConfigMap;
    readonly nextFieldConfig: IpxCredentialFieldConfig;
}): IpxCredentialFieldConfigMap {
    const fieldName = getCredentialFieldNameForElement(input.element);
    const nextFieldConfigMap: IpxCredentialFieldConfigMap = {
        ...input.fieldConfigMap,
    };
    if (fieldName !== input.element.element_key) {
        delete nextFieldConfigMap[input.element.element_key];
    }
    nextFieldConfigMap[fieldName] = {
        ...input.nextFieldConfig,
        field_name: fieldName,
        field_classification:
            input.element.element_kind === IpxWebResourceElementKind.SECRET
                ? IpxCredentialFieldClassification.SECRET
                : IpxCredentialFieldClassification.PUBLIC,
        display: {
            label: fieldName,
        },
    };
    return nextFieldConfigMap;
}

export function buildCredentialFieldConfigMapForDomRpaWorkflow(input: {
    readonly fieldConfigMap: IpxCredentialFieldConfigMap;
    readonly loginWorkflow: IpxDomRpaWorkflow;
    readonly webResourceModel: IpxWebResourceModelDocument;
    readonly fieldNameFallback?: IpxCredentialFieldNameFallback;
}): IpxCredentialFieldConfigMap {
    const nextFieldConfigMap: IpxCredentialFieldConfigMap = {};
    const fieldNameOwners = new Map<string, string>();
    const fieldNameFallback = getCredentialFieldNameFallback(input.fieldNameFallback);
    for (const step of input.loginWorkflow) {
        if (!isCredentialWorkflowStep(step)) {
            continue;
        }
        const element = resolveCredentialWorkflowElement({
            webResourceModel: input.webResourceModel,
            step,
        });
        const fieldName = getWorkflowCredentialFieldNameForElement({
            element,
            fieldNameFallback,
        });
        const existingOwner = fieldNameOwners.get(fieldName);
        if (existingOwner !== undefined && existingOwner !== element.element_key) {
            throw new Error(
                `Display Name "${fieldName}" is used by multiple credential workflow elements.`
            );
        }
        fieldNameOwners.set(fieldName, element.element_key);
        nextFieldConfigMap[fieldName] = buildCredentialFieldConfigForElementWithFallback({
            element,
            fieldConfigMap: input.fieldConfigMap,
            fieldNameFallback,
        });
    }
    return nextFieldConfigMap;
}

export const IpxFlowFieldBindingSource = {
    EFFECTIVE_CREDENTIAL: 'effective_credential',
    PROVISIONAL_CREDENTIAL: 'provisional_credential',
    PROVISIONAL_CONFIRMATION: 'provisional_confirmation',
} as const;
export type IpxFlowFieldBindingSource =
    (typeof IpxFlowFieldBindingSource)[keyof typeof IpxFlowFieldBindingSource];

export interface IpxCredentialFlowFieldBinding {
    readonly credential_field_name: string;
    readonly source: IpxFlowFieldBindingSource;
    readonly element_key: string;
}

export type IpxFlowOutcomePredicateNormalizer = 'trim_v1';

export interface IpxFlowScreenReachedOutcomePredicate {
    readonly kind: 'marker_context_reached';
    readonly required_screen_marker_keys: readonly IpxWebResourceScreenMarkerKey[];
}

export interface IpxFlowMappedTextEqualsOutcomePredicate {
    readonly kind: 'mapped_text_equals';
    readonly element_key: string;
    readonly expected_value: string;
    readonly normalizer: IpxFlowOutcomePredicateNormalizer;
}

export interface IpxFlowMappedTextContainsOutcomePredicate {
    readonly kind: 'mapped_text_contains';
    readonly element_key: string;
    readonly expected_value: string;
    readonly normalizer: IpxFlowOutcomePredicateNormalizer;
}

export interface IpxFlowElementPresentOutcomePredicate {
    readonly kind: 'element_present';
    readonly element_key: string;
}

export interface IpxFlowElementAbsentOutcomePredicate {
    readonly kind: 'element_absent';
    readonly element_key: string;
}

export type IpxFlowOutcomePredicate =
    | IpxFlowScreenReachedOutcomePredicate
    | IpxFlowMappedTextEqualsOutcomePredicate
    | IpxFlowMappedTextContainsOutcomePredicate
    | IpxFlowElementPresentOutcomePredicate
    | IpxFlowElementAbsentOutcomePredicate;

export interface IpxFlowUrlPatternPrerequisite {
    readonly kind: 'url_pattern';
    readonly url_pattern: string;
}

export type IpxFlowUrlPrerequisite = IpxFlowUrlPatternPrerequisite;

export const IpxManualCaptureTriggerKind = {
    FORM_POST_NAVIGATION: 'form_post_navigation',
    FETCH_OR_XHR: 'fetch_or_xhr',
} as const;
export type IpxManualCaptureTriggerKind =
    (typeof IpxManualCaptureTriggerKind)[keyof typeof IpxManualCaptureTriggerKind];

export interface IpxManualCaptureTriggerPolicy {
    readonly trigger_kinds: readonly IpxManualCaptureTriggerKind[];
}

export interface IpxWebResourceModelDocument {
    readonly screen_markers: IpxWebResourceScreenMarkerMap;
    readonly dom_elements: IpxWebResourceDomElementMap;
}

export const IpxWebResourceModelDocumentStorageVersion = {
    V1: 1,
    V2: 2,
    V3: 3,
} as const;
export type IpxWebResourceModelDocumentStorageVersion =
    (typeof IpxWebResourceModelDocumentStorageVersion)[keyof typeof IpxWebResourceModelDocumentStorageVersion];

export interface IpxWebResourceElementEvidenceV1 {
    readonly stable_text?: string | readonly string[];
    readonly stable_attributes?: RpaSanitizedDomStableAttributeMap;
}

export interface IpxWebResourceDomElementDescriptorV1 {
    readonly element_key: string;
    readonly display_name: string;
    readonly selector: RpaDomSelector;
    readonly element_kind: IpxWebResourceElementKind;
    readonly required_screen_marker_keys: readonly IpxWebResourceScreenMarkerKey[];
    readonly evidence?: IpxWebResourceElementEvidenceV1;
    readonly notes?: string;
}

export type IpxWebResourceDomElementMapV1 = Record<string, IpxWebResourceDomElementDescriptorV1>;

export interface IpxWebResourceModelDocumentV1 {
    readonly model_version?: typeof IpxWebResourceModelDocumentStorageVersion.V1;
    readonly screen_markers: IpxWebResourceScreenMarkerMap;
    readonly dom_elements: IpxWebResourceDomElementMapV1;
}

export interface IpxWebResourceModelDocumentV2 {
    readonly model_version: typeof IpxWebResourceModelDocumentStorageVersion.V2;
    readonly screen_markers: IpxWebResourceScreenMarkerMap;
    readonly dom_elements: IpxWebResourceDomElementMapV2;
}

export interface IpxWebResourceModelDocumentV3 extends IpxWebResourceModelDocument {
    readonly model_version: typeof IpxWebResourceModelDocumentStorageVersion.V3;
}

export type StoredIpxWebResourceModelDocument =
    | IpxWebResourceModelDocumentV1
    | IpxWebResourceModelDocumentV2
    | IpxWebResourceModelDocumentV3;

export const IpxWebResourceModelValidationIssueCode = {
    DUPLICATE_LOGIN_WORKFLOW_STEP: 'duplicate_login_workflow_step',
    DUPLICATE_ELEMENT_KEY: 'duplicate_element_key',
    DUPLICATE_DOM_ELEMENT_TARGET: 'duplicate_dom_element_target',
    DUPLICATE_SCREEN_MARKER_TARGET: 'duplicate_screen_marker_target',
    MISSING_SCREEN_MARKER: 'missing_screen_marker',
    MISSING_ELEMENT: 'missing_element',
    WRONG_ELEMENT_KIND: 'wrong_element_kind',
    MISSING_CREDENTIAL_FIELD: 'missing_credential_field',
} as const;
export type IpxWebResourceModelValidationIssueCode =
    (typeof IpxWebResourceModelValidationIssueCode)[keyof typeof IpxWebResourceModelValidationIssueCode];

export interface IpxWebResourceModelValidationIssue {
    readonly code: IpxWebResourceModelValidationIssueCode;
    readonly message: string;
    readonly path: readonly (string | number)[];
}

export interface IpxWebResourceModelValidationResult {
    readonly valid: boolean;
    readonly issues: readonly IpxWebResourceModelValidationIssue[];
}

export interface IpxWebResourceModelDedupeResult {
    readonly document: IpxWebResourceModelDocument;
    readonly screen_marker_key_aliases: ReadonlyMap<
        IpxWebResourceScreenMarkerKey,
        IpxWebResourceScreenMarkerKey
    >;
    readonly dom_element_key_aliases: ReadonlyMap<string, string>;
}

type IpxWebResourceModelElementIndex = Record<string, IpxWebResourceDomElementDescriptor>;

function addIpxWebResourceModelValidationIssue(
    issues: IpxWebResourceModelValidationIssue[],
    issue: IpxWebResourceModelValidationIssue
): void {
    issues.push(issue);
}

function formatScreenMarkerFramePath(framePath: readonly number[] | undefined): string {
    return framePath === undefined ? '' : framePath.join('\u0000');
}

export function fingerprintIpxWebResourceScreenMarkerSelector(selector: RpaDomSelector): string {
    return [
        selector.selector,
        selector.innerText ?? '',
        formatScreenMarkerFramePath(selector.framePath),
    ].join('\u0000');
}

function fingerprintMarkerContext(markerKeys: readonly IpxWebResourceScreenMarkerKey[]): string {
    return [...markerKeys].sort().join('\u0000');
}

export function fingerprintIpxWebResourceDomElementIdentity(
    element: IpxWebResourceDomElementDescriptor
): string {
    return [
        element.element_kind,
        element.selector.selector,
        formatScreenMarkerFramePath(element.selector.framePath),
        fingerprintMarkerContext(element.required_screen_marker_keys),
    ].join('\u0000');
}

function validateScreenMarkerTargets(
    document: IpxWebResourceModelDocument,
    issues: IpxWebResourceModelValidationIssue[]
): void {
    const markerKeyByTarget = new Map<string, string>();
    for (const markerKey of Object.keys(document.screen_markers)) {
        const selector = document.screen_markers[markerKey];
        if (selector === undefined) {
            continue;
        }
        const targetFingerprint = fingerprintIpxWebResourceScreenMarkerSelector(selector);
        const existingMarkerKey = markerKeyByTarget.get(targetFingerprint);
        if (existingMarkerKey !== undefined) {
            addIpxWebResourceModelValidationIssue(issues, {
                code: IpxWebResourceModelValidationIssueCode.DUPLICATE_SCREEN_MARKER_TARGET,
                message: 'Screen marker selectors must be unique',
                path: ['screen_markers', markerKey],
            });
            continue;
        }
        markerKeyByTarget.set(targetFingerprint, markerKey);
    }
}

function validateMarkerContext(input: {
    readonly markerKeys: readonly IpxWebResourceScreenMarkerKey[];
    readonly screenMarkers: IpxWebResourceScreenMarkerMap;
    readonly issues: IpxWebResourceModelValidationIssue[];
    readonly path: readonly (string | number)[];
}): void {
    const seenMarkerKeys = new Set<IpxWebResourceScreenMarkerKey>();
    for (const [markerIndex, markerKey] of input.markerKeys.entries()) {
        if (seenMarkerKeys.has(markerKey)) {
            addIpxWebResourceModelValidationIssue(input.issues, {
                code: IpxWebResourceModelValidationIssueCode.MISSING_SCREEN_MARKER,
                message: 'Screen marker context keys must be unique',
                path: [...input.path, markerIndex],
            });
        }
        seenMarkerKeys.add(markerKey);
        if (input.screenMarkers[markerKey] === undefined) {
            addIpxWebResourceModelValidationIssue(input.issues, {
                code: IpxWebResourceModelValidationIssueCode.MISSING_SCREEN_MARKER,
                message: 'Marker context references an unknown screen marker',
                path: [...input.path, markerIndex],
            });
        }
    }
}

function buildIpxWebResourceModelElementIndex(
    document: IpxWebResourceModelDocument,
    issues: IpxWebResourceModelValidationIssue[]
): IpxWebResourceModelElementIndex {
    const elementIndex: IpxWebResourceModelElementIndex = {};
    const elementKeyByIdentity = new Map<string, string>();
    for (const elementKey of Object.keys(document.dom_elements).sort()) {
        const element = document.dom_elements[elementKey];
        if (element === undefined) {
            continue;
        }
        if (element.element_key !== elementKey) {
            addIpxWebResourceModelValidationIssue(issues, {
                code: IpxWebResourceModelValidationIssueCode.DUPLICATE_ELEMENT_KEY,
                message: 'DOM element descriptor key must match map key',
                path: ['dom_elements', elementKey, 'element_key'],
            });
            continue;
        }
        elementIndex[element.element_key] = element;
        const elementIdentity = fingerprintIpxWebResourceDomElementIdentity(element);
        const existingElementKey = elementKeyByIdentity.get(elementIdentity);
        if (existingElementKey !== undefined) {
            addIpxWebResourceModelValidationIssue(issues, {
                code: IpxWebResourceModelValidationIssueCode.DUPLICATE_DOM_ELEMENT_TARGET,
                message: 'DOM element descriptor targets must be unique',
                path: ['dom_elements', elementKey, 'element_key'],
            });
            continue;
        }
        elementKeyByIdentity.set(elementIdentity, element.element_key);
        validateMarkerContext({
            markerKeys: element.required_screen_marker_keys,
            screenMarkers: document.screen_markers,
            issues,
            path: ['dom_elements', elementKey, 'required_screen_marker_keys'],
        });
    }
    return elementIndex;
}

function canonicalizeMarkerKeys(input: {
    readonly markerKeys: readonly IpxWebResourceScreenMarkerKey[];
    readonly markerKeyAliases: ReadonlyMap<
        IpxWebResourceScreenMarkerKey,
        IpxWebResourceScreenMarkerKey
    >;
}): readonly IpxWebResourceScreenMarkerKey[] {
    const canonicalMarkerKeys: IpxWebResourceScreenMarkerKey[] = [];
    const seenCanonicalMarkerKeys = new Set<IpxWebResourceScreenMarkerKey>();
    for (const markerKey of input.markerKeys) {
        const canonicalMarkerKey = input.markerKeyAliases.get(markerKey) ?? markerKey;
        if (seenCanonicalMarkerKeys.has(canonicalMarkerKey)) {
            continue;
        }
        seenCanonicalMarkerKeys.add(canonicalMarkerKey);
        canonicalMarkerKeys.push(canonicalMarkerKey);
    }
    return canonicalMarkerKeys;
}

export function dedupeIpxWebResourceModelDocument(
    document: IpxWebResourceModelDocument
): IpxWebResourceModelDedupeResult {
    const screenMarkers: IpxWebResourceScreenMarkerMap = {};
    const screenMarkerKeyAliases = new Map<
        IpxWebResourceScreenMarkerKey,
        IpxWebResourceScreenMarkerKey
    >();
    const screenMarkerKeyByIdentity = new Map<string, string>();
    for (const markerKey of Object.keys(document.screen_markers)) {
        const markerSelector = document.screen_markers[markerKey];
        if (markerSelector === undefined) {
            continue;
        }
        const markerIdentity = fingerprintIpxWebResourceScreenMarkerSelector(markerSelector);
        const existingMarkerKey = screenMarkerKeyByIdentity.get(markerIdentity);
        if (existingMarkerKey !== undefined) {
            screenMarkerKeyAliases.set(markerKey, existingMarkerKey);
            continue;
        }
        screenMarkerKeyByIdentity.set(markerIdentity, markerKey);
        screenMarkers[markerKey] = markerSelector;
    }

    const domElements: IpxWebResourceDomElementMap = {};
    const domElementKeyAliases = new Map<string, string>();
    const domElementKeyByIdentity = new Map<string, string>();
    for (const element of listIpxWebResourceDomElements(document)) {
        const canonicalMarkerKeys = canonicalizeMarkerKeys({
            markerKeys: element.required_screen_marker_keys,
            markerKeyAliases: screenMarkerKeyAliases,
        });
        const canonicalElement: IpxWebResourceDomElementDescriptor = {
            ...element,
            required_screen_marker_keys: canonicalMarkerKeys,
        };
        const elementIdentity = fingerprintIpxWebResourceDomElementIdentity(canonicalElement);
        const existingElementKey = domElementKeyByIdentity.get(elementIdentity);
        if (existingElementKey !== undefined) {
            domElementKeyAliases.set(element.element_key, existingElementKey);
            continue;
        }
        domElementKeyByIdentity.set(elementIdentity, element.element_key);
        domElements[canonicalElement.element_key] = canonicalElement;
    }

    return {
        document: {
            screen_markers: screenMarkers,
            dom_elements: domElements,
        },
        screen_marker_key_aliases: screenMarkerKeyAliases,
        dom_element_key_aliases: domElementKeyAliases,
    };
}

function getIpxWebResourceModelElement(
    elementIndex: IpxWebResourceModelElementIndex,
    elementKey: string,
    issues: IpxWebResourceModelValidationIssue[],
    path: readonly (string | number)[]
): IpxWebResourceDomElementDescriptor | null {
    const element = elementIndex[elementKey];
    if (element === undefined) {
        addIpxWebResourceModelValidationIssue(issues, {
            code: IpxWebResourceModelValidationIssueCode.MISSING_ELEMENT,
            message: 'Flow reference points to an unknown DOM element',
            path: [...path, 'element_key'],
        });
        return null;
    }

    return element;
}

function validateIpxDomRpaWorkflowSteps(
    steps: readonly IpxDomRpaWorkflowStep[] | undefined,
    document: IpxWebResourceModelDocument,
    elementIndex: IpxWebResourceModelElementIndex,
    credentialFieldConfigMap: IpxCredentialFieldConfigMap | undefined,
    issues: IpxWebResourceModelValidationIssue[],
    path: readonly (string | number)[]
): void {
    if (steps === undefined) {
        return;
    }
    for (const [stepIndex, step] of steps.entries()) {
        const stepPath = [...path, stepIndex];

        if (step.kind === 'screen_marker') {
            if (document.screen_markers[step.element_key] === undefined) {
                addIpxWebResourceModelValidationIssue(issues, {
                    code: IpxWebResourceModelValidationIssueCode.MISSING_SCREEN_MARKER,
                    message: 'Login workflow step references an unknown screen marker',
                    path: [...stepPath, 'element_key'],
                });
            }
            continue;
        }

        const element = getIpxWebResourceModelElement(
            elementIndex,
            step.element_key,
            issues,
            stepPath
        );
        if (element === null) {
            continue;
        }
        if (step.kind !== 'field' && step.kind !== 'secret') {
            if (
                (step.kind === 'button' &&
                    element.element_kind !== IpxWebResourceElementKind.BUTTON) ||
                (step.kind === 'text' && element.element_kind !== IpxWebResourceElementKind.TEXT)
            ) {
                addIpxWebResourceModelValidationIssue(issues, {
                    code: IpxWebResourceModelValidationIssueCode.WRONG_ELEMENT_KIND,
                    message: 'Login workflow step kind must match the Website Map element kind',
                    path: [...stepPath, 'kind'],
                });
            }
            continue;
        }

        const expectedElementKind =
            step.kind === 'field'
                ? IpxWebResourceElementKind.FIELD
                : IpxWebResourceElementKind.SECRET;
        if (element.element_kind !== expectedElementKind) {
            addIpxWebResourceModelValidationIssue(issues, {
                code: IpxWebResourceModelValidationIssueCode.WRONG_ELEMENT_KIND,
                message: 'Credential workflow steps must target matching credential field elements',
                path: [...stepPath, 'element_key'],
            });
            continue;
        }
        if (
            credentialFieldConfigMap !== undefined &&
            credentialFieldConfigMap[element.display_name] === undefined
        ) {
            addIpxWebResourceModelValidationIssue(issues, {
                code: IpxWebResourceModelValidationIssueCode.MISSING_CREDENTIAL_FIELD,
                message: 'Credential workflow step requires a credential field config binding',
                path: ['credential_field_config', element.display_name],
            });
        }
    }
}

export function validateIpxWebResourceModel(
    document: IpxWebResourceModelDocument
): IpxWebResourceModelValidationResult {
    const issues: IpxWebResourceModelValidationIssue[] = [];
    validateScreenMarkerTargets(document, issues);
    buildIpxWebResourceModelElementIndex(document, issues);

    return {
        valid: issues.length === 0,
        issues,
    };
}

export function validateIpxDomRpaWorkflow(
    document: IpxWebResourceModelDocument,
    workflow: IpxDomRpaWorkflow,
    credentialFieldConfigMap?: IpxCredentialFieldConfigMap
): IpxWebResourceModelValidationResult {
    const issues: IpxWebResourceModelValidationIssue[] = [];
    validateScreenMarkerTargets(document, issues);
    const elementIndex = buildIpxWebResourceModelElementIndex(document, issues);

    validateIpxDomRpaWorkflowSteps(
        workflow,
        document,
        elementIndex,
        credentialFieldConfigMap,
        issues,
        ['credential_field_config']
    );

    return {
        valid: issues.length === 0,
        issues,
    };
}

export interface GenericUxSequenceInit {
    tabId: number;
    steps: GenericUxSequenceStep[];
}
